import { Box, Center, Flex, Group, Loader, Radio, rem, ScrollArea, SimpleGrid, Stack } from '@mantine/core';
import { IconChevronRight } from '@tabler/icons-react';
import { RevisionListTemplatesResponse } from 'api/actions/revision-list-templates/revision-list-templates-response';
import { useApi } from 'api/api-context';
import FormInputGroup from 'components/forms/FormInputGroup';
import Toast from 'components/Toast';
import Button from 'components/Button';
import panic from 'errors/panic';
import { useResponsiveDesign } from 'hooks/use-responsive-design';
import { useDashboardLayout } from 'layouts/dashboard/use-dashboard-layout';
import { useEffect, useMemo, useState } from 'react';

/**
 * Parameters of the TemplateSelect component.
 */
export interface TemplateSelectProps {
  kind: string;
  revisionId: number;
  loading: boolean;
  initialTemplateSlug?: string;
  onChange: ({ template }: { template: string }) => void;
}

type Template = RevisionListTemplatesResponse[number];

/**
 * Allows a technician to select a render-ready revision template.
 */
export default function TemplateSelect({
  revisionId,
  kind,
  loading,
  initialTemplateSlug = '',
  onChange,
}: TemplateSelectProps) {
  const { getAction } = useApi();
  const { usedHeight } = useDashboardLayout();
  const [templateSlug, setTemplateSlug] = useState(initialTemplateSlug);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(true);
  const { isMobile, isTablet } = useResponsiveDesign();

  const initialTemplate = useMemo(
    () => templates.find((template) => template.slug === initialTemplateSlug),
    [initialTemplateSlug, templates]
  );

  useEffect(() => {
    const listTemplates = getAction('RevisionListTemplates');

    setTemplatesLoading(true);
    listTemplates({ parameters: { revisionId: String(revisionId) } })
      .then((response) => {
        setTemplates(response);

        if (response.length === 1) {
          setTemplateSlug(response[0].slug);
        }
      })
      .catch(panic)
      .finally(() => setTemplatesLoading(false));
  }, [getAction, revisionId]);

  return (
    <Flex bg="white" style={{ borderRadius: '4px' }} direction="column" justify="space-between">
      <ScrollArea h={`calc(100vh - ${usedHeight}px - ${rem(88)})`}>
        <Stack p={isTablet ? 20 : 40} spacing={isTablet ? 20 : 40}>
          {templatesLoading ? (
            <Center mih={200}>
              <Loader size="lg" role="status" aria-label="Načítavam dostupné šablóny" />
            </Center>
          ) : (
            <FormInputGroup
              groupTitle={kind === 'revision' ? 'Šablóna revíznej správy' : 'Šablóna formulára'}
              stackProps={{ pl: 0, pr: 0, pb: 0, pt: 0 }}
            >
              {templates.length === 0 ? (
                <Toast
                  type="warning"
                  fullWidth
                  withCloseButton={false}
                  message={
                    kind === 'revision'
                      ? 'Pre tento typ revízie nie je dostupná žiadna šablóna.'
                      : 'Pre tento typ formulára nie je dostupná žiadna šablóna.'
                  }
                />
              ) : (
                <Radio.Group value={templateSlug} onChange={setTemplateSlug}>
                  <SimpleGrid cols={2} spacing={isTablet ? 20 : 40} verticalSpacing={24}>
                    {templates.map(({ name, slug }) => (
                      <Box
                        key={slug}
                        p={24}
                        bg="gray.0"
                        pos="relative"
                        sx={{
                          borderRadius: '4px',
                          border: slug === initialTemplateSlug ? '2px solid var(--mantine-color-red-7)' : '',
                          '&::after': {
                            content: '"Predvolená šablóna"',
                            display: slug === initialTemplateSlug ? 'block' : 'none',
                            position: 'absolute',
                            top: 0,
                            right: 0,
                            background: 'var(--mantine-color-red-7)',
                            color: 'white',
                            padding: `${rem(2)} ${rem(4)}`,
                            borderRadius: `0 0 0 ${rem(4)}`,
                          },
                        }}
                      >
                        <Radio value={slug} label={name} />
                      </Box>
                    ))}
                  </SimpleGrid>
                </Radio.Group>
              )}
              {initialTemplate && templateSlug !== initialTemplateSlug && (
                <Toast
                  type="warning"
                  fullWidth
                  withCloseButton={false}
                  message={
                    kind === 'revision'
                      ? 'Zmenou šablóny sa vymažú všetky vyplnené údaje v revíznej správe.'
                      : 'Zmenou šablóny sa vymažú všetky vyplnené údaje vo formulári.'
                  }
                />
              )}
            </FormInputGroup>
          )}
        </Stack>
      </ScrollArea>
      <Group
        h={88}
        w="100%"
        py={24}
        px={isMobile ? 16 : isTablet ? 20 : 40}
        style={{ borderRadius: '0 0 4px 4px', boxShadow: '0px -4px 12px 0px rgba(197, 198, 211, 0.40)' }}
        position="left"
      >
        <Button
          rightIcon={<IconChevronRight stroke={1.5} size={24} />}
          fullWidth={isMobile}
          onClick={() => onChange({ template: templateSlug })}
          loading={loading}
          disabled={templatesLoading || !templateSlug || !templates.some(({ slug }) => slug === templateSlug)}
        >
          {kind === 'revision' ? 'Pokračovať na revíznu správu' : 'Pokračovať na formulár'}
        </Button>
      </Group>
    </Flex>
  );
}
